import React, { useState, useEffect, useRef } from 'react';
import { addressSuggest, placeDetails } from './traccar';

// Split a US formatted address into parts: "11500 NW 105th St, Miami, FL 33178, USA"
export function splitUsAddress(desc) {
  const parts = String(desc || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length && /^(usa|united states|us)$/i.test(parts[parts.length - 1])) parts.pop();
  const street = parts[0] || '';
  const city = parts[1] || '';
  let state = '', zip = '';
  const sz = parts[2] || '';
  const m = sz.match(/([A-Za-z]{2})\s*(\d{5})?/);
  if (m) { state = (m[1] || '').toUpperCase(); zip = m[2] || ''; }
  if (!zip) { const zm = String(desc).match(/\b(\d{5})\b/); if (zm) zip = zm[1]; }
  return { street, city, state, zip };
}

// Address input with live suggestions as you type. Calls:
//   onChange(text)                        — every keystroke (free text)
//   onPick({ description, lat, lng })     — when a suggestion is chosen (coords when known)
//   onPickParts({ street, city, state, zip, description, lat, lng }) — parsed US parts
export default function AddressAutocomplete({ value, onChange, onPick, onPickParts, placeholder, style, onEnter }) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const skip = useRef(false); // don't re-query right after a pick

  useEffect(() => {
    if (skip.current) { skip.current = false; return undefined; }
    const q = (value || '').trim();
    if (q.length < 3) { setSugs([]); setOpen(false); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      const list = await addressSuggest(q).catch(() => []);
      if (!live) return;
      setSugs(list); setOpen(list.length > 0); setHi(-1);
    }, 280);
    return () => { live = false; clearTimeout(t); };
  }, [value]);

  // close on outside click
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const choose = async (s) => {
    skip.current = true;
    onChange(s.description);
    setOpen(false); setSugs([]);
    let coords = (s.lat != null && s.lng != null) ? { lat: s.lat, lng: s.lng } : null;
    if (!coords && s.placeId) { const d = await placeDetails(s.placeId); if (d) coords = { lat: d.lat, lng: d.lng }; }
    const lat = coords ? coords.lat : null; const lng = coords ? coords.lng : null;
    if (onPickParts) onPickParts({ ...splitUsAddress(s.description), description: s.description, lat, lng });
    if (onPick) onPick({ description: s.description, lat, lng });
  };

  const onKey = (e) => {
    if (open && sugs.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(i + 1, sugs.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' && hi >= 0) { e.preventDefault(); choose(sugs[hi]); return; }
      if (e.key === 'Escape') { setOpen(false); return; }
    }
    if (e.key === 'Enter' && onEnter) onEnter();
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', flex: style && style.flex, minWidth: style && style.minWidth, width: style && style.width }}>
      <input
        style={{ ...style, width: '100%', boxSizing: 'border-box' }}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (sugs.length) setOpen(true); }}
        onKeyDown={onKey}
      />
      {open && sugs.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60, marginTop: '3px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 10px 26px rgba(15,23,42,0.16)', overflow: 'hidden' }}>
          {sugs.map((s, i) => (
            <div
              key={s.placeId || `${s.description}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setHi(i)}
              style={{ padding: '9px 12px', fontSize: '13px', color: '#0f172a', cursor: 'pointer', background: i === hi ? '#eff6ff' : 'white', borderBottom: i < sugs.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', gap: '8px', alignItems: 'flex-start' }}
            >
              <span style={{ flexShrink: 0 }}>📍</span>
              <span>{s.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
